import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Activity,
  Radio,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Save,
  Phone,
  Mail,
  Plus,
  X,
  Copy,
  Trash2,
  Wrench,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Tracker Health — admin dashboard for the CRM-hosted lead-capture tracker.
 *
 * Tells you in one glance whether wisedrive.com is still firing leads into
 * the CRM, and how today's volume compares to the last 7 days. If the script
 * tag falls off the site or anomalous capture rates occur, the watchdog
 * alerts via WhatsApp + email; this page is the visual confirmation surface.
 */
export default function TrackerHealthPage({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState({ whatsapp_recipients: [], email_recipients: [], enabled: true });
  const [cfgLoading, setCfgLoading] = useState(false);
  const [fallbackMobile, setFallbackMobile] = useState('');
  const [fallbackBusy, setFallbackBusy] = useState(false);
  // Maintenance — orphan-draft purge
  const [purgePreview, setPurgePreview] = useState(null); // {identified_orphans, sample[]}
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);

  const runPurgeDryRun = async () => {
    setPurgeBusy(true);
    setPurgeResult(null);
    try {
      const r = await axios.post(
        `${API_URL}/service-leads/maintenance/purge-orphan-drafts?dry_run=true`,
      );
      setPurgePreview(r.data);
      if (r.data.identified_orphans === 0) {
        toast.success('No orphan drafts to clean — DB is already healthy.');
      } else {
        toast.message(`Found ${r.data.identified_orphans} orphan drafts. Review and click "Purge them" to delete.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dry-run failed');
    } finally {
      setPurgeBusy(false);
    }
  };

  const runPurgeForReal = async () => {
    if (!purgePreview?.identified_orphans) return;
    if (!window.confirm(`Delete ${purgePreview.identified_orphans} orphan DRAFT_UNVERIFIED rows? This cannot be undone.`)) return;
    setPurgeBusy(true);
    try {
      const r = await axios.post(`${API_URL}/service-leads/maintenance/purge-orphan-drafts`);
      setPurgeResult(r.data);
      setPurgePreview(null);
      toast.success(`Deleted ${r.data.deleted} orphan drafts.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    } finally {
      setPurgeBusy(false);
    }
  };

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/track/health`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tracker health');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCfg = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/track/alert-config`);
      setCfg(r.data);
    } catch {/* silent */}
  }, []);

  useEffect(() => { fetchHealth(); fetchCfg(); }, [fetchHealth, fetchCfg]);

  const saveCfg = async () => {
    setCfgLoading(true);
    try {
      await axios.post(`${API_URL}/track/alert-config`, cfg);
      toast.success('Alert recipients saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setCfgLoading(false);
    }
  };

  const submitFallback = async () => {
    if (!fallbackMobile || fallbackMobile.replace(/\D/g, '').length < 10) {
      toast.error('Enter a 10-digit mobile');
      return;
    }
    setFallbackBusy(true);
    try {
      const r = await axios.post(`${API_URL}/public/track/capture-fallback`, {
        mobile: fallbackMobile,
        source: 'TRACKER_HEALTH_ADMIN',
        notes: 'Manually inserted from Tracker Health page',
      });
      toast.success(r.data.deduped ? `Reused existing draft ${r.data.lead_id}` : `Captured ${r.data.lead_id}`);
      setFallbackMobile('');
      fetchHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fallback capture failed');
    } finally {
      setFallbackBusy(false);
    }
  };

  const copyEmbedTag = () => {
    const origin = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const tag = `<script async src="${origin}/api/public/track/v1.js"></script>`;
    navigator.clipboard.writeText(tag);
    toast.success('Script tag copied. Paste into wisedrive.com <head>.');
  };

  const verdictColor = (v) => {
    if (v === 'critical') return 'bg-rose-500';
    if (v === 'warning') return 'bg-amber-500';
    if (v === 'no_data') return 'bg-slate-400';
    return 'bg-emerald-500';
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-50'} data-testid="tracker-health-page">
      <div className={embedded ? 'p-6 space-y-6' : 'max-w-[1300px] mx-auto px-6 pt-8 pb-12 space-y-6'}>

        {/* Header — hidden when embedded inside the Monitor page */}
        {!embedded && (
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-semibold text-gray-900 flex items-center gap-2">
                <Activity className="h-7 w-7 text-blue-600" /> Tracker Health
              </h1>
              <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                CRM-hosted lead-capture tracker. Embed once on wisedrive.com; the CRM team owns capture forever.
                Watchdog alerts via WhatsApp + email when the tracker goes silent or capture rate anomalies appear.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchHealth}
              disabled={loading}
              data-testid="tracker-health-refresh"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        )}

        {/* When embedded, show just a compact refresh control */}
        {embedded && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-gray-500 max-w-2xl">
              CRM-hosted lead-capture tracker. Watchdog alerts via WhatsApp + email when the tracker goes
              silent or capture rate anomalies appear.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchHealth}
              disabled={loading}
              data-testid="tracker-health-refresh"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        )}

        {/* Embed-tag card — single thing the website agent ever needs to do */}
        <Card className="p-5 bg-blue-50 border-blue-200" data-testid="embed-tag-card">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
              <Radio className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-blue-900">
                One-time setup — embed this script tag in wisedrive.com &lt;head&gt;
              </div>
              <div className="mt-2 bg-white rounded-md border border-blue-200 px-3 py-2 font-mono text-xs text-gray-800 overflow-auto">
                {`<script async src="${process.env.REACT_APP_BACKEND_URL || 'https://crm.wisedrive.in'}/api/public/track/v1.js"></script>`}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={copyEmbedTag} data-testid="copy-embed-tag">
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy tag
                </Button>
                <span className="text-xs text-blue-800">
                  Tracker version <strong>{data?.tracker_version || '…'}</strong>. Once installed, every page-view pings us within 60s.
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Verdict + today's funnel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-5" data-testid="verdict-card">
            <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Today's capture status</div>
            <div className="flex items-center gap-3 mt-2">
              <div className={`h-3 w-3 rounded-full ${verdictColor(data?.verdict)}`} />
              <div className="text-xl font-semibold capitalize text-gray-900">
                {data?.verdict || 'loading'}
              </div>
            </div>
            <div className="text-sm text-gray-600 mt-3">
              <div>Captures today: <strong>{data?.today?.captures ?? '–'}</strong>{' '}
                <span className="text-gray-400 text-xs">(all statuses)</span></div>
              <div className="text-xs text-gray-500 pl-3">└─ of which drafts (form-start): <strong>{data?.today?.drafts ?? '–'}</strong></div>
              <div className="mt-1">Trailing {data?.trailing?.days ?? 7}-day avg: <strong>{data?.trailing?.captures_avg_per_day ?? data?.trailing?.drafts_avg_per_day ?? '–'} / day</strong></div>
              {data?.pct_of_avg !== null && data?.pct_of_avg !== undefined && (
                <div>Today is at <strong>{data.pct_of_avg}%</strong> of normal</div>
              )}
            </div>
            {data?.is_anomaly && (
              <div className="mt-3 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-xs text-rose-800 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Today is below 30% of trailing avg — WhatsApp + email alert fired at 19:00 IST.</span>
              </div>
            )}
          </Card>

          <Card className="p-5" data-testid="funnel-card">
            <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Today's funnel (IST)</div>
            <div className="mt-3 space-y-2">
              {data?.today?.funnel && Object.keys(data.today.funnel).length > 0 ? (
                Object.entries(data.today.funnel).map(([status, n]) => (
                  <div key={status} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{status}</span>
                    <Badge variant="secondary">{n}</Badge>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-400 italic">No leads today yet</div>
              )}
            </div>
          </Card>

          <Card className="p-5" data-testid="heartbeats-card">
            <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Domain heartbeats</div>
            <div className="mt-3 space-y-3">
              {data?.heartbeats && data.heartbeats.length > 0 ? (
                data.heartbeats.map((hb) => {
                  // Apex shadows (e.g. wisedrive.com → redirects to www).
                  // Backend tags these with hb.shadow_of + un-stales them. We
                  // render dimmed with an info icon so they're visible but
                  // don't trigger user concern.
                  const isShadow = Boolean(hb.shadow_of);
                  return (
                    <div
                      key={hb.domain}
                      className={`flex items-start gap-2 ${isShadow ? 'opacity-60' : ''}`}
                      data-testid={`heartbeat-${hb.domain}`}
                    >
                      {hb.is_stale
                        ? <AlertTriangle className="h-4 w-4 text-rose-500 mt-0.5 flex-shrink-0" />
                        : (isShadow
                            ? <CheckCircle2 className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            : <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />)}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900">{hb.domain}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <Clock className="h-3 w-3" />
                          {hb.minutes_ago !== null
                            ? `${hb.minutes_ago} min ago`
                            : 'never seen'}
                          {hb.tracker_version && <span>· v{hb.tracker_version}</span>}
                          {isShadow && (
                            <span className="text-gray-400 italic">
                              · {hb.status_note || `redirects to ${hb.shadow_of}`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-sm text-rose-600 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>No heartbeats yet — script tag not installed on any domain.</span>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Alert recipients */}
        <Card className="p-5" data-testid="alert-config-card">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-base font-semibold text-gray-900">Alert recipients</div>
              <div className="text-sm text-gray-500 mt-0.5">
                Watchdog fires on stale heartbeat (no ping for 60+ min during 09:00–21:00 IST)
                and daily at 19:00 IST if today &lt; 30% of trailing 7-day avg.
              </div>
            </div>
            <Button onClick={saveCfg} disabled={cfgLoading} data-testid="save-alert-config">
              <Save className="h-4 w-4 mr-1.5" /> Save
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* WhatsApp recipients */}
            <div>
              <div className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-2">
                <Phone className="h-4 w-4 text-emerald-600" /> WhatsApp (E.164)
              </div>
              {(cfg.whatsapp_recipients || []).map((num, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <Input
                    value={num}
                    onChange={(e) => {
                      const next = [...cfg.whatsapp_recipients];
                      next[i] = e.target.value;
                      setCfg({ ...cfg, whatsapp_recipients: next });
                    }}
                    placeholder="+919900112233"
                    data-testid={`whatsapp-recipient-${i}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCfg({
                      ...cfg,
                      whatsapp_recipients: cfg.whatsapp_recipients.filter((_, idx) => idx !== i),
                    })}
                    data-testid={`remove-whatsapp-${i}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCfg({
                  ...cfg,
                  whatsapp_recipients: [...(cfg.whatsapp_recipients || []), ''],
                })}
                data-testid="add-whatsapp-recipient"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add WhatsApp number
              </Button>
            </div>

            {/* Email recipients */}
            <div>
              <div className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-2">
                <Mail className="h-4 w-4 text-blue-600" /> Email
              </div>
              {(cfg.email_recipients || []).map((em, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <Input
                    type="email"
                    value={em}
                    onChange={(e) => {
                      const next = [...cfg.email_recipients];
                      next[i] = e.target.value;
                      setCfg({ ...cfg, email_recipients: next });
                    }}
                    placeholder="ceo@wisedrive.com"
                    data-testid={`email-recipient-${i}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCfg({
                      ...cfg,
                      email_recipients: cfg.email_recipients.filter((_, idx) => idx !== i),
                    })}
                    data-testid={`remove-email-${i}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCfg({
                  ...cfg,
                  email_recipients: [...(cfg.email_recipients || []), ''],
                })}
                data-testid="add-email-recipient"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add email
              </Button>
            </div>
          </div>
        </Card>

        {/* Emergency fallback capture */}
        <Card className="p-5" data-testid="fallback-card">
          <div className="text-base font-semibold text-gray-900">Emergency fallback capture</div>
          <div className="text-sm text-gray-500 mt-0.5 mb-3">
            If a customer reported a number but the tracker / offline-queue both failed,
            paste it here. Idempotent on mobile within 30 min (reuses existing draft).
          </div>
          <div className="flex items-center gap-2 max-w-md">
            <Input
              placeholder="10-digit mobile"
              value={fallbackMobile}
              onChange={(e) => setFallbackMobile(e.target.value)}
              data-testid="fallback-mobile-input"
            />
            <Button
              onClick={submitFallback}
              disabled={fallbackBusy}
              data-testid="fallback-submit"
            >
              Capture
            </Button>
          </div>
        </Card>

        {/* Maintenance — one-shot orphan-draft purge for the pre-2026-05-14
            merge-race leftovers. Idempotent; safe to re-run. */}
        <Card className="p-5 border-amber-200 bg-amber-50/40" data-testid="maintenance-card">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-9 w-9 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
              <Wrench className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-base font-semibold text-gray-900">
                Maintenance: purge orphan drafts
              </div>
              <div className="text-sm text-gray-600 mt-0.5">
                Cleans up duplicate <code className="bg-amber-100 px-1 rounded text-xs">DRAFT_UNVERIFIED</code> rows
                that share a mobile with a downstream sibling (NEW / OTP_PENDING / CONTACTED / …).
                <strong className="text-amber-900"> Merge-and-purge:</strong> any field the sibling is missing
                (e.g. <code className="text-xs">quoted_price</code>, <code className="text-xs">landing_page</code>,
                <code className="text-xs">gclid</code>) is back-filled onto the sibling BEFORE the draft is deleted —
                zero data loss. Idempotent — safe to re-run.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={runPurgeDryRun}
              disabled={purgeBusy}
              data-testid="purge-dry-run-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${purgeBusy ? 'animate-spin' : ''}`} />
              Scan (dry-run)
            </Button>
            {purgePreview && purgePreview.identified_orphans > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={runPurgeForReal}
                disabled={purgeBusy}
                data-testid="purge-apply-btn"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Purge {purgePreview.identified_orphans} orphan{purgePreview.identified_orphans > 1 ? 's' : ''}
              </Button>
            )}
            {purgePreview && (
              <span className="text-xs text-gray-600 ml-1">
                Scanned <strong>{purgePreview.total_drafts_inspected}</strong> drafts ·
                identified <strong>{purgePreview.identified_orphans}</strong> orphan{purgePreview.identified_orphans === 1 ? '' : 's'}
                {purgePreview.would_backfill > 0 && (
                  <> · will back-fill <strong>{purgePreview.would_backfill}</strong> sibling{purgePreview.would_backfill === 1 ? '' : 's'} with funnel data before deleting</>
                )}
              </span>
            )}
            {purgeResult && (
              <span className="text-xs text-emerald-700 ml-1 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Merged <strong>{purgeResult.fields_back_filled}</strong> fields into <strong>{purgeResult.siblings_merged}</strong> sibling{purgeResult.siblings_merged === 1 ? '' : 's'},
                deleted <strong>{purgeResult.deleted}</strong> draft{purgeResult.deleted === 1 ? '' : 's'}. Refresh /service-leads to confirm.
              </span>
            )}
          </div>

          {/* Preview list of what would be deleted */}
          {purgePreview && purgePreview.sample && purgePreview.sample.length > 0 && (
            <div className="mt-4 bg-white rounded-md border border-amber-200 overflow-hidden" data-testid="purge-preview-list">
              <div className="px-3 py-2 bg-amber-100 text-xs font-semibold text-amber-900">
                Preview (first {purgePreview.sample.length} of {purgePreview.identified_orphans})
              </div>
              <div className="max-h-60 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-1.5">Mobile</th>
                      <th className="text-left px-3 py-1.5">DRAFT (to delete)</th>
                      <th className="text-left px-3 py-1.5">Sibling (kept)</th>
                      <th className="text-left px-3 py-1.5">Sibling name</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {purgePreview.sample.map((row) => (
                      <tr key={row.draft_id}>
                        <td className="px-3 py-1.5 font-mono">{row.mobile}</td>
                        <td className="px-3 py-1.5 text-gray-600">
                          {row.draft_id?.slice(0, 8)} · {(row.draft_at || '').slice(0, 19)}
                        </td>
                        <td className="px-3 py-1.5 text-gray-600">
                          {row.sibling_status} · {row.sibling_id?.slice(0, 8)} · {(row.sibling_at || '').slice(0, 19)}
                        </td>
                        <td className="px-3 py-1.5 text-gray-700">{row.sibling_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>

        {/* Recovered captures (offline-queue replays + admin fallbacks) */}
        <Card className="p-5" data-testid="recovered-card">
          <div className="text-base font-semibold text-gray-900 mb-3">
            Recently recovered captures
          </div>
          <div className="text-xs text-gray-500 mb-3">
            Leads that came in via the offline-queue replay path or admin fallback —
            i.e. the original POST failed but we saved it later.
          </div>
          {data?.recovered_captures && data.recovered_captures.length > 0 ? (
            <div className="space-y-2">
              {data.recovered_captures.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0">
                  <div>
                    <div className="font-mono text-gray-800">{r.mobile}</div>
                    <div className="text-xs text-gray-500">
                      {r.status} · {r.created_at?.slice(0, 19)} ·{' '}
                      {r.source_meta?.recovered_via === 'admin_fallback'
                        ? 'admin fallback'
                        : 'offline queue replay'}
                    </div>
                  </div>
                  {r.source_meta?.gclid && (
                    <Badge variant="outline" className="text-[10px] font-mono">gclid</Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-400 italic">
              No recovered captures yet — either the offline queue never fired (rare) or hasn't been needed.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
